import React, { useState } from "react";
import PropTypes from "prop-types";
import { Print } from "./Modals/Print/Print";
import { X, Menu, ArrowLeft, Plus, Pencil, Trash2 } from "lucide-react";
import { Search } from "./Modals/Search/Search";
import Delete from "./Modals/Delete/Delete";
import { Link, useNavigate } from "react-router-dom";

const ActionBar = ({
  title,
  fields,
  apiEndpoint,
  data,
  selectedRow,
  onRowSelect,
  userRole,
  onRefresh,
  onSearch,
  onFilter,
  dropdownData,
  rowKey = "id",
  handleSubmit,
  deleteMessage = "Are you sure you want to delete this item?",
  addLink = "/addIndent",
  editRoute,
  editButtonLabel = "Edit",
  searchQuery = "",
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const canManageItems = [
    "admin",
    "project engineer",
    "pe&so",
    "director",
    "location head",
  ].some((allowedRole) => userRole?.toLowerCase() === allowedRole.toLowerCase());

  const canEditItems = ["purchase head"].some(
    (allowedRole) => userRole?.toLowerCase() === allowedRole.toLowerCase()
  );

  const canDeleteItems = [
    "admin",
  ].some((allowedRole) => userRole?.toLowerCase() === allowedRole.toLowerCase());

  const handleDeleteConfirmation = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
    setIsMobileMenuOpen(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedDeleteId(null);
  };

  const handleSearch = (value) => {
    if (typeof onSearch === 'function') {
      onSearch(value);
    }
  };

  const handleFilter = (filterData) => {
    if (typeof onFilter === 'function') {
      onFilter(filterData);
    }
  };

  // Desktop Action Buttons
  const DesktopActionButtons = () => (
    <div className="hidden md:flex items-center gap-4">
      <div className="flex-grow">
        <Search 
          onSearch={handleSearch}
          onFilter={handleFilter}
          searchQuery={searchQuery}
        />
      </div>
        
        {canManageItems && (
          <Link
            to={addLink}
          className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add New</span>
          </Link>
        )}

        {!canEditItems && (
          <button
            type="button"
            onClick={() => {
              if (selectedRow) {
                navigate(editRoute, { state: { row: selectedRow } });
              } else {
                alert("Please select a row to edit!");
              }
            }}
            disabled={!selectedRow}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
              ${!selectedRow 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-yellow-600 hover:bg-yellow-700'} text-white`}
          >
            <Pencil className="h-4 w-4" />
            <span>{editButtonLabel}</span>
          </button>
        )}

        {canDeleteItems && (
          <button
            type="button"
            onClick={() => selectedRow && handleDeleteConfirmation(selectedRow[rowKey])}
            disabled={!selectedRow}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
              ${!selectedRow 
                ? 'bg-gray-300 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700'} text-white`}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        )}

          <Print data={data} />
    </div>
  );

  // Mobile Action Buttons
  const MobileActionButtons = () => (
    <div className="md:hidden flex flex-col gap-2 w-full">
      <div className="mb-4">
        <Search 
          onSearch={handleSearch}
          onFilter={handleFilter}
          searchQuery={searchQuery}
        />
      </div>

      {canManageItems && (
        <Link
          to={addLink}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors w-full"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Plus className="h-4 w-4" />
          <span>Add New</span>
        </Link>
      )}

      {!canEditItems && (
        <button
          type="button"
          onClick={() => {
            if (selectedRow) {
              navigate(editRoute, { state: { row: selectedRow } });
              setIsMobileMenuOpen(false);
            } else {
              alert("Please select a row to edit!");
            }
          }}
          disabled={!selectedRow}
          className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg w-full transition-colors
            ${!selectedRow 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-yellow-600 hover:bg-yellow-700'} text-white`}
        >
          <Pencil className="h-4 w-4" />
          <span>{editButtonLabel}</span>
        </button>
      )}

      {canDeleteItems && (
        <button
          type="button"
          onClick={() => selectedRow && handleDeleteConfirmation(selectedRow[rowKey])}
          disabled={!selectedRow}
          className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg w-full transition-colors
            ${!selectedRow 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-red-600 hover:bg-red-700'} text-white`}
        >
          <Trash2 className="h-4 w-4" />
          <span>Delete</span>
        </button>
      )}

      <div className="w-full">
        <Print data={data} />
      </div>
    </div>
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
      <button
        type="button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 rounded-lg hover:bg-gray-100"
      >
        {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>
      </div>

      <DesktopActionButtons />
      {isMobileMenuOpen && <MobileActionButtons />}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">{deleteMessage}</h3>
            <div className="flex justify-end gap-3">
              <Delete
                itemId={selectedDeleteId}
                apiEndpoint={apiEndpoint}
                onDelete={() => {
                  onRefresh();
                  handleCancelDelete();
                }}
              />
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ActionBar.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  apiEndpoint: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  selectedRow: PropTypes.object,
  onRowSelect: PropTypes.func,
  userRole: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  onFilter: PropTypes.func,
  dropdownData: PropTypes.array,
  rowKey: PropTypes.string,
  handleSubmit: PropTypes.func,
  deleteMessage: PropTypes.string,
  addLink: PropTypes.string,
  editRoute: PropTypes.string,
  editButtonLabel: PropTypes.string,
  searchQuery: PropTypes.string,
};

export default ActionBar;